import { Component } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  ApplicationPaths,
  QueryParameterNames,
} from "./AuthorizationConstants";
import authService from "./AuthorizeService";
import {
  TerminatedUser,
  AccessForbidden,
} from "../../pages/TerminatedUser.tsx";
import { useACL } from "@ui/components/document/DocumentACL";
import { useEmployeeSelf } from "../../services/useEmployee";
import { Lock, AlertTriangle } from "lucide-react";
import { Spinner, Center } from "@chakra-ui/react";

class AuthorizeRoute extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ready: false,
      authenticated: false,
    };
  }

  componentDidMount() {
    this._subscription = authService.subscribe(() =>
      this.authenticationChanged(),
    );
    this.populateAuthenticationState();

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          this.setState({
            authenticated: false,
          });
        }

        return Promise.reject(error);
      },
    );
  }

  componentWillUnmount() {
    authService.unsubscribe(this._subscription);
  }

  async populateAuthenticationState() {
    const authenticated = await authService.isAuthenticated();
    this.setState({ ready: true, authenticated });
  }

  async authenticationChanged() {
    this.setState({ ready: false, authenticated: false });
    await this.populateAuthenticationState();
  }

  render() {
    const { ready, authenticated } = this.state;
    const {
      element,
      isPermitted,
      desktopOnly,
      requiredRoles,
      excludeCategories,
      employee,
    } = this.props;

    if (!ready) {
      return (
        <Center h="100dvh">
          <Spinner size="lg" />
        </Center>
      );
    }

    if (!authenticated) {
      var link = document.createElement("a");
      link.href = window.location.href;

      const returnUrl = `${link.protocol}//${link.host}${link.pathname}${link.search}${link.hash}`;
      const redirectUrl = `${ApplicationPaths.Login}?${
        QueryParameterNames.ReturnUrl
      }=${encodeURIComponent(returnUrl)}`;

      return <Navigate replace to={redirectUrl} />;
    }

    if (requiredRoles && requiredRoles.length > 0) {
      const hasAccess = isPermitted(requiredRoles);
      if (!hasAccess) {
        return (
          <AccessForbidden
            title="Access Forbidden"
            description="You do not have permission to view this page."
            icon={Lock}
          />
        );
      }
    }

    const isUserAgentMobile = /Mobi|Android|iPhone|iPad/i.test(
      navigator.userAgent,
    );
    if (desktopOnly && isUserAgentMobile) {
      return (
        <AccessForbidden
          title="Desktop Only"
          description="This screen is available only on desktop devices."
          icon={AlertTriangle}
        />
      );
    }

    if (excludeCategories && excludeCategories.length > 0) {
      const employeeCategory = employee?.category?.label?.toLowerCase();
      if (excludeCategories.includes(employeeCategory)) {
        return (
          <AccessForbidden
            title="Access Forbidden"
            description="You do not have permission to view this page."
            icon={Lock}
          />
        );
      }
    }

    return <TerminatedUser>{element}</TerminatedUser>;
  }
}

export function WithACLAuthorizeRoute(props) {
  const { isPermitted } = useACL();
  const { data: employee, isLoading: isEmployeeLoading } = useEmployeeSelf();

  if (isEmployeeLoading) {
    return (
      <Center h="100dvh">
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <AuthorizeRoute {...props} isPermitted={isPermitted} employee={employee} />
  );
}
